import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Users, Lock, Eye, SearchX } from 'lucide-react'
import { useChats } from '@/hooks/useChat'
import { cn, formatRelativeTime, gravatarUrl, truncate } from '@/lib/utils'
import type { Chat } from '@/types/chat'

/**
 * ChatList component - displays the list of user's chats.
 *
 * Features:
 * - Shows unread count with visual indicator
 * - Displays last message preview
 * - Icons for group chats, read-only, and public chats
 */

interface ChatListProps {
  onChatSelect?: (chatId: string) => void
  searchTerm?: string
}

export function ChatList({ onChatSelect, searchTerm = '' }: ChatListProps) {
  const { roomId: selectedChatId } = useParams()
  const { data: chats, isLoading, error } = useChats()

  // Filter and sort chats by last activity
  const sortedChats = useMemo(() => {
    if (!chats) return []

    // Apply search filter
    const normalizedSearch = searchTerm.toLowerCase().trim()
    const filtered = normalizedSearch
      ? chats.filter((chat) => {
          // Match against chat name
          if (chat.chatName?.toLowerCase().includes(normalizedSearch)) return true
          // Match against participant usernames
          if (chat.participants.some((p) => p.username.toLowerCase().includes(normalizedSearch)))
            return true
          // Match against owner username
          if (chat.owner.username.toLowerCase().includes(normalizedSearch)) return true
          return false
        })
      : chats

    return [...filtered].sort((a, b) => {
      // Chats with unread messages first
      if (a.numOfUnreadMessages > 0 && b.numOfUnreadMessages === 0) return -1
      if (a.numOfUnreadMessages === 0 && b.numOfUnreadMessages > 0) return 1

      // Then by last message date
      const aDate = a.lastMessage?.dateSent || a.dateCreated
      const bDate = b.lastMessage?.dateSent || b.dateCreated
      return new Date(bDate).getTime() - new Date(aDate).getTime()
    })
  }, [chats, searchTerm])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-center text-sm text-danger">
        Failed to load chats. Please try again.
      </div>
    )
  }

  if (!sortedChats.length) {
    // Show different message if filtering vs. no chats at all
    if (searchTerm) {
      return (
        <div className="p-8 text-center text-text-light">
          <SearchX className="mx-auto mb-2 h-12 w-12 opacity-50" />
          <p>No chats found</p>
          <p className="text-sm">Try a different search term</p>
        </div>
      )
    }
    return (
      <div className="p-8 text-center text-text-light">
        <Users className="mx-auto mb-2 h-12 w-12 opacity-50" />
        <p>No chats yet</p>
        <p className="text-sm">Start a conversation to see it here</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col divide-y divide-gray-100">
      {sortedChats.map((chat) => (
        <ChatListItem
          key={chat.id}
          chat={chat}
          isSelected={chat.id === selectedChatId}
          onSelect={onChatSelect}
        />
      ))}
    </div>
  )
}

interface ChatListItemProps {
  chat: Chat
  isSelected: boolean
  onSelect?: (chatId: string) => void
}

function ChatListItem({ chat, isSelected, onSelect }: ChatListItemProps) {
  // Build display name
  const displayName = chat.chatName || chat.participants.map((p) => p.username).join(', ')

  // Get last message info
  const lastMessagePreview = chat.lastMessage
    ? `${chat.lastMessage.sender.username}: ${truncate(chat.lastMessage.content, 40)}`
    : 'No messages yet'

  const lastMessageTime = chat.lastMessage?.dateSent || chat.dateCreated

  const handleClick = () => {
    onSelect?.(chat.id)
  }

  return (
    <Link
      to={`/chat/${chat.id}`}
      onClick={handleClick}
      className={cn(
        'flex gap-3 p-3 transition-colors',
        'hover:bg-gray-50',
        isSelected && 'bg-primary-50 hover:bg-primary-50',
        chat.numOfUnreadMessages > 0 && 'bg-primary-50/50'
      )}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <img
          src={gravatarUrl(chat.owner.gravatarHash, 48)}
          alt={chat.owner.username}
          className="h-12 w-12 rounded-full"
        />
        {/* Chat type indicator */}
        {chat.groupChat && (
          <span className="absolute -bottom-1 -right-1 rounded-full bg-nav-dark p-1">
            <Users className="h-3 w-3 text-white" />
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header row */}
        <div className="flex items-center gap-2">
          {/* Chat name */}
          <span
            className={cn(
              'flex-1 truncate font-medium',
              chat.numOfUnreadMessages > 0 && 'font-semibold'
            )}
          >
            {truncate(displayName, 30)}
          </span>

          {/* Indicators */}
          {chat.readonly && (
            <span title="Read-only">
              <Lock className="h-3.5 w-3.5 flex-shrink-0 text-text-light" />
            </span>
          )}
          {chat.publicChat && (
            <span title="Public chatroom">
              <Eye className="h-3.5 w-3.5 flex-shrink-0 text-text-light" />
            </span>
          )}

          {/* Time */}
          <span className="flex-shrink-0 text-xs text-text-light">
            {formatRelativeTime(lastMessageTime)}
          </span>
        </div>

        {/* Last message preview */}
        <div className="flex items-center gap-2">
          <p
            className={cn(
              'flex-1 truncate text-sm text-text-light',
              chat.numOfUnreadMessages > 0 && 'font-medium text-text'
            )}
          >
            {lastMessagePreview}
          </p>

          {/* Unread badge */}
          {chat.numOfUnreadMessages > 0 && (
            <span className="flex-shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white">
              {chat.numOfUnreadMessages > 99 ? '99+' : chat.numOfUnreadMessages}
            </span>
          )}
        </div>

        {/* Participants (for group chats) */}
        {chat.groupChat && chat.participants.length > 0 && (
          <p className="mt-1 truncate text-xs text-text-muted">
            {chat.participants.map((p) => p.username).join(', ')}
          </p>
        )}
      </div>
    </Link>
  )
}
